import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-04 `/v1/change-log*` — read-only admin audit feed. Mutation surface is
// limited to `POST :id/undo` which flips status to 'undone'. Types kept local
// per the existing api/*.ts convention.

export interface ChangeLogEntry {
  id: string;
  /** ISO-8601 timestamp string, or null when the row has no time recorded. */
  time: string | null;
  actor: string | null;
  /** Display string: '<action> · <target_id>' (rendered as-is). */
  action: string | null;
  /** Raw action code (e.g. 'users.update'); for client-side filtering. */
  actionCode?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  /** null = active (Undo button enabled). 'undone' = reverted (badge shown). */
  status: string | null;
  /** uuid of the *.undo entry that reverted this row, when applicable. */
  revertedByEntryId?: string | null;
}

export interface ChangeLogList {
  data: ChangeLogEntry[];
  total: number;
}

/**
 * Single entry + raw before/after snapshots. Used by the undo dialog to show a
 * field-level diff of what the undo will restore. `*Truncated` = the snapshot
 * was >50 KB and dropped, so it can't be previewed or cleanly undone.
 */
export interface ChangeLogEntryDetail extends ChangeLogEntry {
  beforeJson: Record<string, unknown> | null;
  afterJson: Record<string, unknown> | null;
  beforeTruncated: boolean;
  afterTruncated: boolean;
}

export interface ChangeLogListParams {
  limit?: number;
  offset?: number;
}

const BASE = "/v1/change-log";

/** GET /v1/change-log — paginated, newest first. */
export async function listChangeLog(
  params: ChangeLogListParams = {},
): Promise<ChangeLogList> {
  const res = await Api.get(BASE, { params });
  return unwrap<ChangeLogList>(res.data);
}

/** GET /v1/change-log/:id — single entry with before/after snapshots. */
export async function getChangeLogEntry(id: string): Promise<ChangeLogEntryDetail> {
  const res = await Api.get(`${BASE}/${encodeURIComponent(id)}`);
  return unwrap<ChangeLogEntryDetail>(res.data);
}

/**
 * POST /v1/change-log/:id/undo — flips status to 'undone'.
 *
 * `force=true` re-issues the undo skipping the backend's state-conflict check
 * (the 409 `change-log-state-mismatch` guard). The UI offers this only after a
 * plain undo was refused for that reason, so an admin is never dead-ended the
 * way the POC's check-free undo never was.
 */
export async function undoChangeLog(id: string, force = false): Promise<{ success: true }> {
  const res = await Api.post(
    `${BASE}/${encodeURIComponent(id)}/undo${force ? "?force=true" : ""}`,
  );
  return unwrap<{ success: true }>(res.data);
}
